import FormattedDatePickerReportsV3 from "../FormattedDatePickerReportsV3";
import { FilterChip } from "../components/FilterChip";
import { DATE_FILTER_INPUT_WIDTH } from "../types/constants";
import {
  DEFAULT_DATE_FORMAT,
  formatDateValue,
  getDatePresetRange,
  getDynamicDateDisplayName,
} from "../utils/dateUtils";
import DynamicDatePresetSection from "./DynamicDatePresetSection";
import StaticDatePresetSection from "./StaticDatePresetSection";
import { Box, Button, MenuItem, MenuList, Typography } from "@mui/material";
import { reportsV3DynamicDatePresets } from "config";
import * as labels from "config.labels";
import React from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const DateFilter = ({
  pendingRange,
  selectedPreset,
  formatDateInputValue,
  parseDateInputValue,
  onDateRangeChange,
  showActions = false,
  onSave,
  onCancel,
  blankCount = 0,
  blankSelected = false,
  onToggleBlank,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const saveLabel = translateLabels(labels.DATE_FILTER_SAVE, {
    default: "Save",
  });
  const cancelLabel = translateLabels(labels.DATE_FILTER_CANCEL, {
    default: "Cancel",
  });

  const handleFromDateChange = (nextFrom) => {
    onDateRangeChange({
      from: nextFrom,
      to: pendingRange?.to ?? null,
    });
  };
  const handleToDateChange = (nextTo) => {
    onDateRangeChange({
      from: pendingRange?.from ?? null,
      to: nextTo,
    });
  };
  const handleSelectPreset = (preset) => {
    const range = getDatePresetRange(preset);
    onDateRangeChange({
      from: range.from,
      to: range.to,
    });
  };
  const handleBlankClick = () => {
    if (!blankSelected) {
      onDateRangeChange({ from: null, to: null });
    }
    onToggleBlank?.();
  };
  const handleSelectDynamic = (preset) => {
    onDateRangeChange({
      from: null,
      to: null,
      dynamic: { parameters: preset.parameters, name: preset.defaultLabel },
    });
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          alignItems: "center",
          justifyContent: "space-around",
        }}
      >
        <FormattedDatePickerReportsV3
          label={translateLabels(labels.DATE_FILTER_FROM, { default: "From" })}
          type={"date"}
          compact
          clearable={false}
          sx={{
            width: DATE_FILTER_INPUT_WIDTH,
          }}
          value={
            pendingRange?.from ? formatDateInputValue(pendingRange.from) : ""
          }
          onChangeDate={(value) => {
            const nextFrom = value ? parseDateInputValue(value) : null;
            handleFromDateChange(nextFrom);
          }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  enabled: false,
                },
              ],
            },
          }}
        />
        <FormattedDatePickerReportsV3
          label={translateLabels(labels.DATE_FILTER_TO, { default: "To" })}
          type={"date"}
          compact
          clearable={false}
          sx={{
            width: DATE_FILTER_INPUT_WIDTH,
          }}
          value={pendingRange?.to ? formatDateInputValue(pendingRange.to) : ""}
          onChangeDate={(value) => {
            const nextTo = value ? parseDateInputValue(value) : null;
            handleToDateChange(nextTo);
          }}
          slotProps={{
            popper: {
              placement: "bottom-start",
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  enabled: false,
                },
              ],
            },
          }}
        />
      </Box>
      {showActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 1.5,
            pt: 1,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Button
            size={"small"}
            variant={"contained"}
            onClick={onSave}
            fullWidth
            disabled={
              pendingRange?.from != null &&
              pendingRange?.to != null &&
              pendingRange.to < pendingRange.from
            }
          >
            {saveLabel}
          </Button>
          <Button
            size={"small"}
            variant={"contained"}
            color={"secondary"}
            onClick={onCancel}
            fullWidth
          >
            {cancelLabel}
          </Button>
        </Box>
      )}
      <MenuList
        dense
        sx={{ flex: 1, minHeight: 0, overflow: "auto", width: "100%" }}
      >
        {blankCount > 0 && (
          <MenuItem
            onClick={handleBlankClick}
            selected={blankSelected}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: blankSelected
                ? "action.selected"
                : "transparent",
              "&:hover": {
                backgroundColor: blankSelected
                  ? "action.focus"
                  : "action.hover",
              },
            }}
          >
            <Typography
              variant={"body2"}
              sx={{
                fontWeight: blankSelected ? 600 : 400,
                fontSize: "0.875rem",
              }}
            >
              {"<blank>"}
            </Typography>
          </MenuItem>
        )}
        <DynamicDatePresetSection
          selectedName={pendingRange?.dynamic?.name}
          onSelect={handleSelectDynamic}
        />
        <StaticDatePresetSection
          selectedPreset={selectedPreset}
          onSelectPreset={handleSelectPreset}
        />
      </MenuList>
    </React.Fragment>
  );
};

export const renderDateFilterChips = (
  dateRange,
  dateFormat,
  onClearFrom,
  onClearTo,
  translateLabels,
  onClearBlank,
  onClearDynamic
) => {
  const chips = [];
  if (dateRange.dynamic) {
    chips.push(
      <FilterChip
        key={"dynamic"}
        label={getDynamicDateDisplayName(
          reportsV3DynamicDatePresets(),
          dateRange.dynamic.name,
          translateLabels
        )}
        onDelete={onClearDynamic}
        sx={{ ml: 1, mr: 0.5 }}
      />
    );
  }
  if (dateRange.isEmpty) {
    chips.push(
      <FilterChip
        key={"blank"}
        label={"<blank>"}
        onDelete={onClearBlank}
        sx={{ ml: 1, mr: 0.5 }}
      />
    );
  }
  if (dateRange.from) {
    const formattedDate = formatDateValue(
      dateRange.from,
      dateFormat || DEFAULT_DATE_FORMAT
    );
    chips.push(
      <FilterChip
        key={"from"}
        label={`${
          translateLabels
            ? translateLabels(labels.DATE_FILTER_CHIP_FROM, { default: "from" })
            : "from"
        } ${formattedDate}`}
        onDelete={onClearFrom}
        sx={{
          ml: 1,
          mr: 0.5,
        }}
      />
    );
  }
  if (dateRange.to) {
    const formattedDate = formatDateValue(
      dateRange.to,
      dateFormat || DEFAULT_DATE_FORMAT
    );
    chips.push(
      <FilterChip
        key={"to"}
        label={`${
          translateLabels
            ? translateLabels(labels.DATE_FILTER_CHIP_TO, { default: "to" })
            : "to"
        } ${formattedDate}`}
        onDelete={onClearTo}
        sx={{
          ml: 0.5,
          mr: 1,
        }}
      />
    );
  }
  return chips.length > 0 ? chips : null;
};

export default DateFilter;
